import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-5 py-4 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-orange";

export default function LoginForm({ title, subtitle = "Acesso restrito", prefix = "admin", onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      data-testid={`${prefix}-login-form`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="mx-auto w-full max-w-md border border-white/10 bg-navy/40 p-8 backdrop-blur-sm lg:p-10"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center border border-brand-orange/50 text-brand-orange">
          <Lock size={18} />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-white">{title}</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{subtitle}</p>
        </div>
      </div>
      <div className="mt-8 space-y-5">
        <label className="block">
          <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">E-mail</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={`${prefix}-login-email`}
            className={inputCls}
            placeholder="seu e-mail"
          />
        </label>
        <label className="block">
          <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Senha</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={`${prefix}-login-password`}
            className={inputCls}
            placeholder="sua senha"
          />
        </label>
        {error && (
          <p data-testid={`${prefix}-login-error`} className="border-l-2 border-brand-red pl-4 text-sm text-brand-red">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid={`${prefix}-login-submit`}
          className="flex w-full items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </div>
    </motion.form>
  );
}
